import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { qduelsApi, useGetRatingQuery } from '@/services/qduelsApi';
import { listVerdict } from '@/lib/listState';
import { useStalled, useEverFailed, NO_ANSWER_MS } from '@/hooks/useStalled';
import { PanelNotice, PanelWaiting } from '@/components/hub/PanelNotice';
import { useGetWalletsQuery } from '@/services/qbankApi';
import { env } from '@/env';
import { useAppDispatch } from '@/store/hooks';
import { useHaptics } from '@/hooks/useHaptics';
import { QueueSheet } from '@/components/social/QueueSheet';
import { RankedCircle } from '@/components/duels/RankedCircle';
import type { QduelsRating } from '@/types/qduels';

/**
 * Riga sotto il titolo: modalità e rank insieme ("1v1 - Gold").
 *
 * Stanno su una riga sola perché rispondono alla stessa domanda — "in che cosa
 * sono, e a che livello". Separarli costringerebbe a leggere due volte.
 */
function RankedModeAndTier({ rating }: { rating: QduelsRating | undefined }) {
  const { t } = useTranslation('duels');
  const tier = rating?.tier?.name ?? t('ranked.unranked', { defaultValue: 'Non classificato' });
  return <span className="truncate text-sm font-semibold text-fg-muted">1v1 - {tier}</span>;
}

/** QBank code of the 1v1 rating coin — the wallet carrying the Elo and its level. */
const RATING_COIN_CODE = 'QRAT';

/**
 * 1v1 Ranked — the ranked circle: tier progress and the placement checklist
 * around a central Play button.
 *
 * The rating fields were already coming down from qduels and the client threw
 * them away; nothing here needed a backend change.
 */
export function RankedPanel() {
  const { t } = useTranslation('hub');
  const dispatch = useAppDispatch();
  const haptics = useHaptics();
  const [queueOpen, setQueueOpen] = useState(false);

  const { data: rating, isLoading, isError } = useGetRatingQuery();

  // 🔴 QUESTO PANNELLO NON AVEVA ALCUN RAMO D'ERRORE, ed era il piu' costoso degli otto.
  // Misurato su `stg-tma` il 2026-09-06 (or45): senza risposta da qduels lo schermo
  // affermava «1v1 - Non classificato», «Lv. —» e teneva VIVO il bottone TROVA PARTITA —
  // che non e' una lettura: entra in una coda ranked e lascia un ELO scritto. Un invito a
  // toccare qualcosa che il backend non puo' servire e' peggio di un errore.
  //
  // ⚠️ «Non classificato» resta la verita' per chi e' davvero senza rank: quel caso e' un
  // **200 con `tier: null`**, non un errore. La distinzione la fa la RISPOSTA, non l'assenza
  // di dati — che e' precisamente cio' che qui mancava.
  const answered = rating !== undefined;
  const everFailed = useEverFailed(isError, answered);
  const pending = !answered && !everFailed;
  const stalled = useStalled(pending);
  const noAnswer = useStalled(pending, NO_ANSWER_MS);
  const verdict = listVerdict(rating ? 1 : 0, [
    {
      reachable: true,
      loading: isLoading,
      slow: stalled,
      failed: everFailed || noAnswer,
      answered,
    },
  ]);

  // The wallet LEVEL for the RATING coin. The qduels XP *is* the QRAT balance in
  // QBank; the level is computed by QBank on that wallet. One coin, two reads —
  // the rating query owns the number, the wallet query owns the level.
  //
  // Matched by CODE, not by "the first wallet that happens to have a level".
  // QRAT is the only EXP coin seeded today, so the loose version worked by
  // accident — but the league reference is a SECOND EXP coin, unseeded only
  // because nobody has configured it yet (`QBANK_LEADERBOARD_REFERENCE_COIN_ID`
  // is empty, and seeding it is step one of the economy plan). The day it lands,
  // the loose lookup would print another coin's level next to the Elo and
  // nothing would look wrong.
  const walletsQ = useGetWalletsQuery(undefined, { skip: !env.featureQbankEnabled });
  const wallets = walletsQ.data ?? [];
  const leveled = wallets.filter((w) => w.level != null);
  const ratingWallet =
    leveled.find((w) => w.code?.toUpperCase() === RATING_COIN_CODE) ??
    // Fallback only while there is exactly one EXP coin: if the code is ever
    // renamed we still show a level, but we never guess between two.
    (leveled.length === 1 ? leveled[0] : undefined);
  const expLevel = ratingWallet?.level ?? null;

  /**
   * The payoff lands on the way OUT.
   *
   * A match settles inside the sheet, so the rating behind it is already stale
   * by the time the player closes it. Invalidating on close refetches once and
   * lets the new tick light up as the circle comes back — after the game, which
   * is when it means something. Polling while the sheet is open would spend
   * requests updating something nobody can see.
   */
  const closeQueue = () => {
    setQueueOpen(false);
    dispatch(qduelsApi.util.invalidateTags(['Rating']));
    haptics.impact('light');
  };

  if (verdict === 'loading' || verdict === 'slow') {
    // Il cerchio in stato di caricamento TACE: nessuna intestazione, perche' l'intestazione
    // e' fatta di affermazioni («Non classificato», «Lv. —») che non abbiamo ancora il
    // diritto di fare. Oltre la soglia di pazienza il cerchio resta e sotto compare la riga
    // che dice che ci sta mettendo troppo — non che e' andata male.
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 pt-3">
        <RankedCircle rating={undefined} loading onPlay={() => {}} />
        {/* Lo spinner qui e' gia' il cerchio: `spinner={false}` evita di disegnarne due. */}
        {verdict === 'slow' && <PanelWaiting slow spinner={false} className="h-auto" />}
      </div>
    );
  }
  if (verdict !== 'rows') {
    // Niente `<RankedCircle>` e niente `<QueueSheet>`: senza il foglio non esiste il
    // bottone che entra in coda, ed e' voluto — non si offre un'azione che scrive verso un
    // servizio che non ha risposto.
    return <PanelNotice tone="error" />;
  }

  return (
    <div className="flex flex-col gap-4 pt-3">
      {/* Intestazione su due colonne: a sinistra CHI SEI (sezione + modalità e
          rank), a destra QUANTO VALI (livello + EXP). Prima era una pila
          centrata sopra il cerchio; separare identità e progresso rende
          leggibili entrambi in un colpo d'occhio invece di impilarli. */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col">
          <span className="text-2xl font-extrabold leading-tight text-fg">
            {t('sections.ranked', { defaultValue: 'Ranked' })}
          </span>
          <RankedModeAndTier rating={rating} />
        </div>

        <div className="flex shrink-0 flex-col items-end">
          <span className="text-2xl font-extrabold leading-tight text-fg">
            {expLevel != null ? `Lv. ${expLevel}` : '—'}
          </span>
          <span className="text-sm font-semibold tabular-nums text-fg-muted">
            {rating?.xp != null ? `${rating.xp.toLocaleString()} EXP` : ''}
          </span>
        </div>
      </div>

      {/* Il cerchio resta centrato per conto suo: l'intestazione sopra occupa
          tutta la larghezza, quindi il contenitore non può più centrare tutto. */}
      <div className="flex justify-center">
        <RankedCircle rating={rating} loading={isLoading} onPlay={() => setQueueOpen(true)} />
      </div>

      {/* Niente `queueOpen &&` davanti: smontando alla chiusura il pannello
          sparirebbe prima di poter scivolare via. La visibilità la governa il
          foglio, che resta montato per la durata dell'uscita. */}
      <QueueSheet open={queueOpen} onClose={closeQueue} />
    </div>
  );
}
